import React, { useContext, useState, useEffect } from "react";
import { AuthContext } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button, TextField, Box, Typography, Divider } from "@mui/material";
import { login as loginApi } from "../api/auth"; // Importiere die abstrahierte Login-Funktion
import { getApiBaseUrl, setApiBaseUrl } from "../api/config";
import PasswordResetModal from "../components/Auth/PasswordResetModal";
import { Capacitor } from '@capacitor/core';
import QRCodeScanner from "../components/Token/QRCodeScanner";
import { Modal, Input, message, Space } from 'antd';
import { QrcodeOutlined, SettingOutlined, SaveOutlined } from '@ant-design/icons';

const LoginPage = () => {
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState({ username: "", password: "" });
  const [error, setError] = useState(""); // Zustand für Fehlermeldungen
  const [currentApiUrl, setCurrentApiUrl] = useState("");
  const [passwordResetOpen, setPasswordResetOpen] = useState(false);
  const [qrScannerOpen, setQrScannerOpen] = useState(false);
  const [apiSettingsOpen, setApiSettingsOpen] = useState(false);
  const [newApiUrl, setNewApiUrl] = useState("");
  const isNativePlatform = Capacitor.isNativePlatform();

  // Load and display current API URL
  useEffect(() => {
    const loadApiUrl = async () => {
      const url = await getApiBaseUrl();
      setCurrentApiUrl(url);
      console.log("LoginPage: Current API URL:", url);
    };
    loadApiUrl();
  }, []);

  const handleLogin = async () => {
    try {
      setError(""); // Fehler zurücksetzen
      console.log("LoginPage: Starting login with credentials:", { username: credentials.username });

      const data = await loginApi(credentials); // Verwende die abstrahierte Funktion
      console.log("LoginPage: Received data from loginApi:", JSON.stringify(data));
      console.log("LoginPage: Data type:", typeof data, "Keys:", Object.keys(data || {}));

      await login(data); // Speichere das Token im AuthContext und lade User-Info
      console.log("LoginPage: Login successful, navigating to dashboard");
      navigate("/dashboard");
    } catch (error) {
      console.error("Login failed:", error);
      console.error("Error type:", typeof error);
      console.error("Error keys:", Object.keys(error || {}));
      console.error("Error details:", error.response?.data);
      console.error("Error status:", error.response?.status);
      console.error("Error message:", error.message);

      let errorMessage = "Login fehlgeschlagen. ";
      if (error.response?.status === 401) {
        errorMessage += "Ungültige Anmeldedaten.";
      } else if (error.response?.status === 404) {
        errorMessage += "API Endpoint nicht gefunden. Bitte überprüfe die API URL in den Settings.";
      } else if (error.message) {
        errorMessage += error.message;
      } else {
        errorMessage += "Bitte überprüfe deine Anmeldedaten und die API-Verbindung.";
      }

      setError(errorMessage);
    }
  };

  // API-URL speichern
  const handleSaveApiUrl = async () => {
    if (!newApiUrl.trim()) {
      message.error('Bitte eine URL eingeben');
      return;
    }
    try {
      await setApiBaseUrl(newApiUrl.trim());
      setCurrentApiUrl(newApiUrl.trim());
      setApiSettingsOpen(false);
      message.success('API-URL gespeichert');
    } catch (err) {
      message.error('Fehler beim Speichern der API-URL');
    }
  };

  // API-Settings Modal öffnen
  const openApiSettings = () => {
    setNewApiUrl(currentApiUrl);
    setApiSettingsOpen(true);
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      height="100vh"
      sx={{ backgroundColor: "#f5f5f5" }} // Optional: Hintergrundfarbe
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 400,
          padding: 3,
          borderRadius: 2,
          boxShadow: 3,
          backgroundColor: "white",
        }}
      >
        <Typography variant="h4" gutterBottom align="center">
          Anmelden
        </Typography>
        {currentApiUrl && (
          <Typography variant="caption" color="textSecondary" align="center" sx={{ display: 'block', marginBottom: 1 }}>
            API: {currentApiUrl}
          </Typography>
        )}
        {error && (
          <Typography color="error" gutterBottom align="center">
            {error}
          </Typography>
        )}
        <TextField
          label="Benutzername"
          value={credentials.username}
          onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
          margin="normal"
          fullWidth
        />
        <TextField
          label="Passwort"
          type="password"
          value={credentials.password}
          onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
          margin="normal"
          fullWidth
        />
        <Button variant="contained" color="primary" onClick={handleLogin} fullWidth>
          Login
        </Button>

        {/* QR-Code Login für Mobile App */}
        {isNativePlatform && (
          <>
            <Divider sx={{ my: 2 }}>oder</Divider>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => setQrScannerOpen(true)}
              fullWidth
              startIcon={<QrcodeOutlined />}
            >
              Mit QR-Code anmelden
            </Button>
          </>
        )}

        <Box sx={{ marginTop: 2, textAlign: 'center', display: 'flex', justifyContent: 'space-between' }}>
          <Button variant="text" size="small" onClick={() => setPasswordResetOpen(true)}>
            Passwort vergessen?
          </Button>
          <Button variant="text" size="small" onClick={openApiSettings} startIcon={<SettingOutlined />}>
            Server
          </Button>
        </Box>
      </Box>

      <PasswordResetModal
        open={passwordResetOpen}
        onClose={() => setPasswordResetOpen(false)}
      />

      {/* QR-Code Scanner Modal */}
      <Modal
        title="QR-Code Login"
        open={qrScannerOpen}
        onCancel={() => setQrScannerOpen(false)}
        footer={null}
        destroyOnClose
      >
        <QRCodeScanner
          onSuccess={() => {
            setQrScannerOpen(false);
            navigate("/dashboard");
          }}
          onClose={() => setQrScannerOpen(false)}
        />
      </Modal>

      {/* API-Settings Modal */}
      <Modal
        title={<><SettingOutlined /> Server-Einstellungen</>}
        open={apiSettingsOpen}
        onCancel={() => setApiSettingsOpen(false)}
        onOk={handleSaveApiUrl}
        okText="Speichern"
        cancelText="Abbrechen"
        okButtonProps={{ icon: <SaveOutlined /> }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <Typography variant="body2" style={{ marginBottom: 8 }}>
              API-Server URL:
            </Typography>
            <Input
              value={newApiUrl}
              onChange={(e) => setNewApiUrl(e.target.value)}
              placeholder="https://api.example.com"
              size="large"
            />
          </div>
          <Typography variant="caption" color="textSecondary">
            Geben Sie die URL des API-Servers ein, mit dem Sie sich verbinden möchten.
            Beispiel: https://api.billard.flogra.de
          </Typography>
        </Space>
      </Modal>
    </Box>
  );
};

export default LoginPage;